import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import useTranslation from 'next-translate/useTranslation';
import { useIsDesktop, useIsMobile } from '../../style/constants';
import Flex from '../common/Flex';
import { Comment, RootState, useAlerts, useCurrentUser } from '../../api';
import CommentForm from '../TrackDetail/Comments/CommentForm';
import { CenterLoader } from '../common/Miscellanea';
import CommentsForPost from './Comments/CommentsForPost';
import { CommunityPostInfo } from '../../api/lib/api/musicKnowledgeHub';
import PostCard from './PostCard';
import { TextBolder12 } from '../../VuexyDesign/atoms/Text';
import LoadMoreButton from '../../VuexyDesign/atoms/LoadMoreButton';
import RenderMetaTags from '../../utils/renderMetaTags';
import BrowserPageTitle from '../common/BrowserPageTitle';
import { ToastError } from '../Toast';

export interface StateProps {
  userId?: number;
  comments: Comment[];
  isFetchingComments: boolean;
  userIsLoggedIn: boolean;
  currentPage: number;
  lastPageReached: boolean;
  totalComments?: number;
}

export interface DispatchProps {
  fetchTrackComments: (page?: number, perPage?: number) => void;
  sendTrackComment: (comment: string) => void;
  deleteComment: (
    commentId: number,
    parent: {
      parentCommentId?: number;
    },
  ) => void;
}

export interface OwnProps {
  postInfoData: CommunityPostInfo;
}

type Props = StateProps & DispatchProps & OwnProps;

function BlogPost({
  userIsLoggedIn,
  userId,
  sendTrackComment,
  comments,
  deleteComment,
  lastPageReached,
  isFetchingComments,
  currentPage,
  fetchTrackComments,
  postInfoData,
}: Props) {
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const user = useCurrentUser();
  const { errors } = useAlerts();
  const commentsSorted = [...comments.filter((comment) => !comment.feedback)];
  const showLoadMoreBtn = !isFetchingComments && !lastPageReached;
  const { t } = useTranslation('musicKnowledgeHub');
  const getTotalCommentCount = useSelector(
    (state: RootState) => state?.musicKnowledgeHub.totalCommentsCountData,
  );
  const loadMore = () => {
    if (!lastPageReached && !isFetchingComments) {
      fetchTrackComments(currentPage + 1, 20);
    }
  };
  useEffect(() => {
    if (errors.length > 0) {
      ToastError(errors[0]);
    }
  }, [errors]);

  return (
    <BlogPostContainer className={`${isDesktop ? 'px-2' : ''}`}>
      {postInfoData && (
        <>
          <BrowserPageTitle description={`${postInfoData.seo_description}`}>
            {`${postInfoData.seo_title}`}
          </BrowserPageTitle>
          {RenderMetaTags({
            'og:title': `${postInfoData.seo_title}`,
            'og:description': `${postInfoData.seo_description}`,
            'og:type': 'Post',
            'og:url': `https://virpp.com/music-knowledge-hub/${postInfoData.slug}`,
            'twitter:title': `${postInfoData.seo_title}`,
            'twitter:description': `${postInfoData.seo_description}`,
            keywords: `${postInfoData.seo_keywords}`,
          })}
        </>
      )}
      <Flex mb="0.5rem" flexDirection="column" justifyContent="center">
        <PostCard postInfoData={postInfoData} />
        <div className="mt-3">
          {getTotalCommentCount > 0 && (
            <div className="text-uppercase mb-1">
              <TextBolder12>
                {getTotalCommentCount} {t('musicKnowledgeHub:comments')}
              </TextBolder12>
            </div>
          )}
          {user && user.id && (
            <TrackDescription>
              <CommentForm onSend={sendTrackComment} />
            </TrackDescription>
          )}

          {comments && (
            <TrackDescription style={{ paddingTop: isMobile ? 18 : 10 }}>
              {commentsSorted.map((comment) => (
                <CommentsForPost
                  {...comment}
                  userId={userId}
                  deleteComment={deleteComment}
                  userIsLoggedIn={userIsLoggedIn}
                  postId={postInfoData.id}
                  key={comment.id}
                />
              ))}
              {showLoadMoreBtn && (
                <>
                  <div className="d-flex justify-content-center mt-1">
                    <LoadMoreButton onClick={loadMore} />
                  </div>
                </>
              )}

              {isFetchingComments && <CenterLoader />}
            </TrackDescription>
          )}
        </div>
      </Flex>
    </BlogPostContainer>
  );
}

const BlogPostContainer = styled.div`
  margin-bottom: 2rem;
`;
const TrackDescription = styled.div`
  display: flex;
  flex-direction: column;
`;
export default BlogPost;
